import { z } from 'zod';

/** API contracts of chat (docs/design/P6E_CHAT.md): shared by the server and the web app. */

const person = z.object({ id: z.uuid(), name: z.string() });

export const MAX_MESSAGE = 4000;

/** Realtime events pushed to browsers (Socket.IO). */
export const CHAT_EVENTS = {
  message: 'chat:message',
  messageUpdated: 'chat:message-updated',
  channel: 'chat:channel',
  read: 'chat:read',
  typing: 'chat:typing',
} as const;

export const CHANNEL_KINDS = ['group', 'direct', 'unit', 'project'] as const;

/** Reactions people can add (a fixed set keeps the picker small and the data clean). */
export const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '✅', '🙏'] as const;

export const channelSummarySchema = z.object({
  id: z.uuid(),
  kind: z.enum(CHANNEL_KINDS),
  /** Group name, or the other person's name for direct messages. */
  name: z.string(),
  description: z.string().nullable(),
  isPrivate: z.boolean(),
  /** The other person of a direct conversation. */
  other: person.nullable(),
  unread: z.number().int(),
  /** Unread messages that name the viewer (or any unread in a direct conversation). */
  mentions: z.number().int(),
  lastMessage: z
    .object({ author: z.string(), body: z.string(), deleted: z.boolean(), at: z.string() })
    .nullable(),
  lastReadAt: z.string(),
  muted: z.boolean(),
  memberCount: z.number().int(),
  /** Unit / project gone or archived: read-only. */
  archived: z.boolean(),
  /** Page of the unit / project of an automatic channel. */
  link: z.string().nullable(),
});
export type ChannelSummary = z.infer<typeof channelSummarySchema>;
export const channelListSchema = z
  .object({ items: z.array(channelSummarySchema) })
  .meta({ id: 'ChatChannelList' });
export type ChannelList = z.infer<typeof channelListSchema>;

export const channelSchema = channelSummarySchema
  .extend({
    members: z.array(person.extend({ role: z.enum(['admin', 'member']) })),
    /** The viewer is a member (public groups can be previewed before joining). */
    joined: z.boolean(),
    /** manage: members and name (own groups only); pin: pin messages (channel admins, both in direct). */
    can: z.object({ manage: z.boolean(), post: z.boolean(), pin: z.boolean() }),
  })
  .meta({ id: 'ChatChannel' });
export type Channel = z.infer<typeof channelSchema>;

export const createGroupSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).nullable().optional(),
    isPrivate: z.boolean().default(true),
    memberIds: z.array(z.uuid()).max(500).default([]),
  })
  .meta({ id: 'CreateChatGroup' });
export type CreateGroup = z.input<typeof createGroupSchema>;

export const updateGroupSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).nullable().optional(),
    isPrivate: z.boolean(),
  })
  .meta({ id: 'UpdateChatGroup' });
export type UpdateGroup = z.infer<typeof updateGroupSchema>;

export const membersChangeSchema = z
  .object({
    add: z.array(z.uuid()).max(500).default([]),
    remove: z.array(z.uuid()).max(500).default([]),
  })
  .meta({ id: 'ChatMembersChange' });
export type MembersChange = z.input<typeof membersChangeSchema>;

export const fileRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  mimeType: z.string(),
  size: z.number(),
});

export const messageSchema = z
  .object({
    id: z.uuid(),
    channelId: z.uuid(),
    author: person,
    /** Empty when recalled. */
    body: z.string(),
    mentions: z.array(z.uuid()),
    files: z.array(fileRefSchema),
    editedAt: z.string().nullable(),
    deleted: z.boolean(),
    createdAt: z.string(),
    /** Root message of the thread this one answers. */
    parentId: z.uuid().nullable(),
    /** Replies of a root message. */
    replyCount: z.number().int(),
    lastReplyAt: z.string().nullable(),
    pinned: z.boolean(),
    reactions: z.array(z.object({ emoji: z.string(), users: z.array(person) })),
  })
  .meta({ id: 'ChatMessage' });
export type Message = z.infer<typeof messageSchema>;

export const messagePageSchema = z
  .object({
    items: z.array(messageSchema),
    /** Older messages exist: pass the first item's id as `before`. */
    hasMore: z.boolean(),
  })
  .meta({ id: 'ChatMessagePage' });
export type MessagePage = z.infer<typeof messagePageSchema>;
export const messagePageQuerySchema = z.object({
  before: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const sendMessageSchema = z
  .object({
    body: z.string().trim().max(MAX_MESSAGE).default(''),
    mentions: z.array(z.uuid()).max(50).default([]),
    files: z.array(z.uuid()).max(10).default([]),
    /** Answer in the thread of this message. */
    parentId: z.uuid().optional(),
  })
  .refine((m) => m.body.length > 0 || m.files.length > 0, { message: 'Tin nhắn trống' })
  .meta({ id: 'SendChatMessage' });
export type SendMessage = z.input<typeof sendMessageSchema>;

export const threadSchema = z
  .object({ root: messageSchema, items: z.array(messageSchema) })
  .meta({ id: 'ChatThread' });
export type Thread = z.infer<typeof threadSchema>;

export const reactSchema = z.object({ emoji: z.enum(REACTIONS) }).meta({ id: 'ChatReact' });
export type React = z.infer<typeof reactSchema>;

export const pinSchema = z.object({ pinned: z.boolean() }).meta({ id: 'ChatPin' });

export const pinListSchema = z
  .object({ items: z.array(messageSchema) })
  .meta({ id: 'ChatPinList' });
export type PinList = z.infer<typeof pinListSchema>;

export const editMessageSchema = z
  .object({
    body: z.string().trim().min(1).max(MAX_MESSAGE),
    mentions: z.array(z.uuid()).max(50).default([]),
  })
  .meta({ id: 'EditChatMessage' });
export type EditMessage = z.input<typeof editMessageSchema>;

export const unreadSchema = z
  .object({ unread: z.number().int(), mentions: z.number().int() })
  .meta({ id: 'ChatUnread' });
export type Unread = z.infer<typeof unreadSchema>;

export const chatPeopleSchema = z
  .object({ items: z.array(person.extend({ email: z.string() })) })
  .meta({ id: 'ChatPeople' });
export type ChatPeople = z.infer<typeof chatPeopleSchema>;

/** Payload of `chat:typing` pushed to the other members. */
export interface TypingEvent {
  channelId: string;
  user: { id: string; name: string };
}
